<template>
    <div v-if="courtSitting">
        <b-form-row>
            <b-form-group label="Sitting Date/Time"
                          label-for="sittingDayDateTimeDisplayText"
                          class="col-md-4">
                <b-form-input name="sittingDayDateTimeDisplayText"
                              :value="courtSitting.sittingDayDateTimeDisplayText"
                              readonly>
                </b-form-input>
            </b-form-group>
            <b-form-group label="Presiding Officer/s"
                          label-for="courtJudges"
                          class="col-md-5">
                <select2 name="courtJudges"
                         disabled
                         multiple
                         :options="courtJudges"
                         key-property="courtJudgeId"
                         text-property="displayName"
                         :value="courtJudgeIds"></select2>
            </b-form-group>
            <b-form-group label="Court Room"
                          label-for="courtLocationRoomName"
                          class="col-md-3">
                <b-form-input name="courtLocationRoomName"
                              :value="courtSitting.courtLocationRoomName"
                              readonly>
                </b-form-input>
            </b-form-group>

            <b-form-group :label="$t('Entities.Shared.CourtSittingMatterType')"
                          label-for="courtSittingMatterTypeName"
                          :class="$config.enableAudioQualityOptions ? 'col-md-2' : 'col-md-3'">
                <b-form-input name="courtSittingMatterTypeName"
                              :value="courtSitting.courtSittingMatterTypeName"
                              readonly>
                </b-form-input>
            </b-form-group>
            <b-form-group label="Sitting Mode"
                          label-for="modeOfSitting"
                          class="col-md-2">
                <b-form-input name="modeOfSitting"
                              :value="courtSitting.modeOfSittingOptionsDisplayText"
                              readonly>
                </b-form-input>
            </b-form-group>
            <b-form-group v-if="$config.enableAudioQualityOptions"
                          label="Audio Quality"
                          label-for="audioQuality"
                          class="col-md-2 align-self-end">
                <b-input-group>
                    <b-form-input name="audioQuality"
                                  :value="courtSitting.audioQualityOptionsDisplayText"
                                  readonly>
                    </b-form-input>
                    <b-input-group-append v-if="canModifyCourtSittingAudioQuality">
                        <b-button variant="outline-secondary" v-b-tooltip title="Edit Audio Quality" v-on:click="editAudioQuality"><i class="fas fa-edit"></i></b-button>
                    </b-input-group-append>
                </b-input-group>
            </b-form-group>
            <b-form-group label="Restricted"
                          label-for="restricted"
                          class="col-md-2 align-self-end">
                <b-input-group>
                    <b-form-input name="restricted"
                                  :value="courtSitting.restrictedDisplayText"
                                  readonly
                                  :class="courtSitting.restricted ? 'text-danger' : ''">
                    </b-form-input>
                    <b-input-group-append v-if="canModifyCourtSittingRestrictedMode">
                        <b-button variant="outline-secondary" v-b-tooltip title="Edit Restricted Mode" v-on:click="editRestricted"><i class="fas fa-edit"></i></b-button>
                    </b-input-group-append>
                </b-input-group>
            </b-form-group>
            <b-form-group label="Mandatory Request"
                          label-for="mandatoryRequest"
                          class="col-md-2 align-self-end">
                <b-input-group>
                    <b-form-input name="mandatoryRequest"
                                  :value="courtSitting.mandatoryRequestDisplayText"
                                  readonly>
                    </b-form-input>
                </b-input-group>
            </b-form-group>
            <b-form-group label="Audio Assessment"
                          label-for="audioAssessment"
                          class="col-md-2">
                <b-form-input name="audioAssessment"
                              :value="courtSitting.audioAssessmentDisplayText"
                              readonly>
                </b-form-input>
            </b-form-group>
        </b-form-row>

        <b-modal size="sm"
                 centered
                 title="Edit Restricted Mode"
                 v-model="editRestrictedDialogOpen"
                 v-on:hidden="$emit('modalOpen', false)"
                 v-on:shown="$emit('modalOpen', true)"
                 v-on:ok.prevent="updateRestricted">
            <b-form-row>
                <b-form-group label="Restricted"
                              label-for="currentRestricted"
                              class="col">
                    <b-form-checkbox ref="currentRestricted"
                                     name="currentRestricted"
                                     v-model="currentRestricted"
                                     class="mb-2"
                                     switch>
                    </b-form-checkbox>
                </b-form-group>
            </b-form-row>
        </b-modal>

        <b-modal size="sm"
                 centered
                 title="Edit Audio Quality"
                 v-model="editAudioQualityDialogOpen"
                 v-on:hidden="$emit('modalOpen', false)"
                 v-on:shown="$emit('modalOpen', true)"
                 v-on:ok.prevent="updateAudioQuality">
            <b-form-row>
                <b-form-group label="Audio Quality"
                              label-for="currentAudioQuality"
                              class="col">
                    <select2 ref="currentAudioQuality"
                             name="currentAudioQuality"
                             allow-clear
                             in-b-modal
                             placeholder="Select an audio quality"
                             v-model="currentAudioQuality"
                             :options="allAudioQualityOptions">
                    </select2>
                </b-form-group>
            </b-form-row>
        </b-modal>
    </div>
</template>

<script>
    import Vue from 'vue';
    import BootstrapVue from 'bootstrap-vue';
    import axios from 'axios';

    import ErrorsMixin from '../mixins/ErrorsMixin';

    Vue.use(BootstrapVue);

    export default {
        name: 'CourtSittingDetails',
        mixins: [ErrorsMixin],
        props: {
            apiControllerUrl: {
                type: String,
                required: true
            },
            courtSittingId: {
                type: String,
                required: true
            },
            canModifyCourtSittingRestrictedMode: {
                type: Boolean,
                default: false
            },
            canModifyCourtSittingAudioQuality: {
                type: Boolean,
                default: false
            },
            allAudioQualityOptions: {
                type: Array,
                default: null
            },
        },
        data: function () {
            return {
                courtSitting: null,
                currentErrors: [],                
                editRestrictedDialogOpen: false,
                currentRestricted: false,         
                editAudioQualityDialogOpen: false,
                currentAudioQuality: null,
                courtJudges: null
            };
        },
        computed: {
            courtJudgeIds() {
                var courtJudgeIds = [];
                if (this.courtJudges != null) {
                    this.courtJudges.forEach(courtJudge => {
                        courtJudgeIds.push(courtJudge.courtJudgeId);
                    });
                }
                return courtJudgeIds;
            }
        },
        methods: {
            reloadSitting: function () {
                this.currentErrors = [];
                if (this.courtSittingId) {
                    return axios
                        .get(this.apiControllerUrl + '/' + this.courtSittingId)
                        .then(response => {
                            this.courtSitting = response.data;
                        })
                        .catch(ex => {
                            this.currentErrors = this.handleAxiosException(ex);
                        })
                        .finally(() => {
                            if (this.currentErrors.length != 0) {
                                this.showErrorsToast(this.currentErrors);
                            }
                        });
                }
                return Promise.resolve();
            },            
            reload: function () {
                var promises = [];
                promises.push(this.reloadSitting());
                promises.push(this.reloadJudges());
                return Promise.all(promises);
            },
            editRestricted: function () {
                this.currentRestricted = this.courtSitting.restricted;
                this.editRestrictedDialogOpen = true;
            },
            updateRestricted: function () {
                this.currentErrors = [];
                axios
                    .put(this.apiControllerUrl + '/' + this.courtSittingId + '/restricted', JSON.stringify(this.currentRestricted),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(() => {
                        this.reload();
                    })
                    .catch(ex => {
                        this.currentErrors = this.handleAxiosException(ex);
                    })
                    .finally(() => {
                        if (this.currentErrors.length == 0) {
                            this.editRestrictedDialogOpen = false;
                        }
                    });
            },      
            editAudioQuality: function () {
                this.currentAudioQuality = this.courtSitting.audioQualityOptionsList ? this.courtSitting.audioQualityOptionsList[0] : null,
                this.editAudioQualityDialogOpen = true;
            },
            updateAudioQuality: function () {
                this.currentErrors = [];

                var data = {
                    audioQuality: this.currentAudioQuality ? this.currentAudioQuality : null
                };

                axios
                    .put(this.apiControllerUrl + '/' + this.courtSittingId + '/audioquality', JSON.stringify(data),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(() => {
                        this.reload();
                    })
                    .catch(ex => {
                        this.currentErrors = this.handleAxiosException(ex);
                    })
                    .finally(() => {
                        if (this.currentErrors.length == 0) {
                            this.editAudioQualityDialogOpen = false;
                        }
                    });
            },
            reloadJudges: function () {
                    var currentErrors = [];
                    if (this.courtSittingId) {
                        return axios
                            .get(this.$urlHelper.getApiUrl('courtjudge', 'bycourtsitting', [this.courtSittingId]))
                            .then(response => {
                                this.courtJudges = response.data;
                            })
                            .catch(ex => {
                                currentErrors = this.handleAxiosException(ex);
                            })
                            .finally(() => {
                                if (currentErrors.length != 0) {
                                    this.showErrorsToast(currentErrors);
                                }
                            });
                    }
                    return Promise.resolve();
                }
            },

        mounted: function () {
            this.reload();
        }
    }
</script>